import { Box, Skeleton, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { useMember } from "api/members";
import { usePoints } from "api/points";
import { useAppSelector } from "app/hooks";
import { BoxSection } from "components/BoxSection/BoxSection";
import { ReactComponent as ChevronRightIcon } from "components/Icons/chevron-right.svg";
import { UnitTransfersModals } from "components/Layout/Unit/UnitTransfersList/UnitTransfersModals";
import { UnitTransfersModalsActions } from "components/Layout/Unit/UnitTransfersList/helpers";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { OLTabs } from "components/Tabs/OLTabs";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { MEMBER_UNIT_TRANSFER_LIST_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { TransferSearchType, UnitTransferModal } from "types/unit-transfers";
import { alphabetizeByProperty } from "utilities/arrays";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatNumberValue } from "utilities/numbers";
import { handleBooleanValue, pickPluralizedUnit } from "utilities/strings";
import { useStyles } from "./SingleMember.styles";

const TransfersTable = ({ selectedWalletId }: { selectedWalletId: string }) => {
  const [selectedModal, setModal] = useState<UnitTransferModal>();
  const [open, setOpen] = useState(false);
  const { listing, updateListing } = useListing(MEMBER_UNIT_TRANSFER_LIST_ID);
  const { setColumnOrdering } = useTablesStateHelpers();
  const { message } = useMessage();
  const { usePointsTransferList } = usePoints();

  function createItems(items: TransferSearchType[]): Row[] {
    return items?.map((item: TransferSearchType) => ({
      cells: [
        { content: item.transferId, property: "transferId" },
        { content: item.type, property: "type" },
        {
          content: message({ id: handleBooleanValue(!!item.cancelled) }),
          property: "cancelled",
        },
        {
          content: message({ id: handleBooleanValue(!!item.pending) }),
          property: "pending",
        },
        {
          align: "right",
          content: formatNumberValue(item.value),
          emptyString: "0",
          property: "value",
        },
        { content: item.comment, property: "comment", dataHjSuppress: true },
        { content: OLDate(item.createdAt), property: "createdAt" },
        { content: OLDate(item.registeredOn), property: "registeredOn" },
        { content: OLDate(item.expiresAt), property: "expiresAt" },
        {
          content: OLDate(item.actionCause?.performedAt),
          property: "actionCause:performedAt",
        },
        { content: OLDate(item.unlockedAt), property: "unlockedAt" },
        {
          content: item.unlockedAt ? "-" : OLDate(item.lockedUntil),
          property: "lockedUntil",
        },
      ],
      id: item.transferId,
      metadata: {
        cancelled: item.cancelled,
        expiresAt: item.expiresAt,
        pending: item.pending,
        type: item.type,
      },
      testId: "unit-transfers-table-row",
    }));
  }

  const { data: pointsTransfers } = usePointsTransferList({
    filters: selectedWalletId
      ? [
          {
            condition: "eq",
            query: "walletId",
            value: selectedWalletId,
          },
        ]
      : [],
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
  });

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "transferID" }),
          property: "transferId",
          isSortable: true,
        },
        {
          headerText: message({ id: "type" }),
          property: "type",
          isSortable: true,
        },
        {
          headerText: message({ id: "cancelled" }),
          property: "cancelled",
          isSortable: true,
        },
        {
          headerText: message({ id: "pending" }),
          property: "pending",
          isSortable: true,
        },
        {
          align: "right",
          headerText: message({ id: "value" }),
          property: "value",
          isSortable: true,
        },
        {
          headerText: message({ id: "comment" }),
          property: "comment",
          isSortable: true,
        },
        {
          headerText: message({ id: "createdOn" }),
          property: "createdAt",
          isSortable: true,
          tooltip: (
            <ExternalLinkTooltipContent
              name="createdOn"
              text={<span>{message({ id: "createdOnInfo" })}</span>}
            />
          ),
        },
        {
          headerText: message({ id: "registeredOn" }),
          property: "registeredOn",
          isSortable: true,
          tooltip: (
            <ExternalLinkTooltipContent
              name="registeredOn"
              text={<span>{message({ id: "registeredOnInfo" })}</span>}
            />
          ),
        },
        {
          headerText: message({ id: "expiresOn" }),
          property: "expiresAt",
          isSortable: true,
        },
        {
          headerText: message({ id: "performedOn" }),
          property: "actionCause:performedAt",
          isSortable: true,
          isVisible: false,
        },
        {
          headerText: message({ id: "unlockedOn" }),
          property: "unlockedAt",
          isSortable: true,
        },
        {
          headerText: message({ id: "lockedUntil" }),
          property: "lockedUntil",
          isSortable: true,
        },
      ]),
    [message],
  );

  const items = pointsTransfers?.items
    ? createItems(pointsTransfers.items)
    : [];

  return (
    <>
      <Box mt={8} />
      <OLTable
        actions={{
          body: UnitTransfersModalsActions({ setOpen, setModal }),
        }}
        setColumnOrdering={setColumnOrdering}
        tableName={MEMBER_UNIT_TRANSFER_LIST_ID}
        data-test-id="single-member-unit-transfers"
        columns={pointsTransfers && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        rows={pointsTransfers && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        pagination={{
          count: calculateCount(
            pointsTransfers?.total.filtered,
            pointsTransfers?.total.all,
          ),
          onChangePage: (page) => {
            updateListing({
              payload: page,
              type: ListingActions.CHANGE_PAGE,
            });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      <UnitTransfersModals
        open={open}
        selectedModal={selectedModal}
        close={() => setOpen(false)}
        setModal={(modal: UnitTransferModal) => setModal(modal)}
      />
    </>
  );
};

export function Wallets({ fullView }: { fullView?: boolean }) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: memberID } = useParams<{ id: string }>();
  const { useMemberWalletsData } = useMember(memberID);
  const { data: memberWalletsData } = useMemberWalletsData({
    rows: 50,
  });
  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const defaultWalletId = memberWalletsData?.items.find(
    ({ walletType }) => walletType.code === defaultWallet.code,
  )?.walletId;
  const [selectedWalletId, setSelectedWalletId] = useState<false | string>(
    false,
  );
  const currentWallet = memberWalletsData?.items.find(
    (wallet) => wallet.walletId === selectedWalletId,
  );

  const walletType = currentWallet?.walletType;
  const walletAccount = currentWallet?.account;

  const isGeneralInfoCurrentPath =
    location.pathname === links.singleMemberGeneralInfo(memberID);

  const prepareMemberUnitsLimitationValue = (value?: string) => {
    switch (value) {
      case "noLimit":
        return message({ id: "unlimited" });
      case "calendarHours":
        return message({ id: "perHour" });
      case "calendarDays":
        return message({ id: "perDay" });
      case "calendarWeeks":
        return message({ id: "perWeek" });
      case "calendarMonths":
        return message({ id: "perMonth" });
      case "calendarYears":
        return message({ id: "perYear" });
      case "inTotal":
        return message({ id: "inTotal" });
      default:
        return "-";
    }
  };

  const walletUnitsItems = [
    {
      label: "pending",
      value: formatNumberValue(walletAccount?.lockedUnits),
    },
    {
      label: "spent",
      value: formatNumberValue(walletAccount?.spentUnits),
    },
    {
      label: "expired",
      value: formatNumberValue(walletAccount?.expiredUnits),
    },
    {
      label: "totalEarned",
      value: formatNumberValue(walletAccount?.earnedUnits),
    },
    {
      label: "memberLimitation",
      value: prepareMemberUnitsLimitationValue(
        walletType?.limits?.pointsPerMember?.interval?.type,
      ),
    },
    {
      label: "leftToAchieveLimit",
      value: formatNumberValue(currentWallet?.unitsLimitRemaining),
    },

    {
      label: "limit",
      value: formatNumberValue(walletType?.limits?.pointsPerMember?.value),
    },
    {
      label: "currentEarnings",
      value: formatNumberValue(currentWallet?.unitsLimitUsed),
    },
  ];
  const digitalWalletItems: {
    id: string;
    label: string;
  }[] = useMemo(
    () =>
      alphabetizeByProperty(
        memberWalletsData?.items?.map(({ walletId, walletType }) => ({
          id: walletId,
          label: walletType.name,
        })),
        "label",
      ),
    [memberWalletsData],
  );

  useEffect(() => {
    if (defaultWalletId) {
      setSelectedWalletId(defaultWalletId);
    }
  }, [memberWalletsData, defaultWalletId]);

  return (
    <>
      <Box className={classes.titleSection}>
        <Typography
          className={classes.header}
          data-test-id="single-member-digital-wallets-title"
        >
          {digitalWalletItems?.length === 1
            ? digitalWalletItems?.[0]?.label
            : message({ id: "wallets" })}
        </Typography>
        {isGeneralInfoCurrentPath && (
          <OLButton
            endIcon={<ChevronRightIcon />}
            onClick={() => navigate(links.singleMemberWallets(memberID))}
          >
            {message({ id: "viewAll" }).toLocaleUpperCase()}
          </OLButton>
        )}
      </Box>
      {!!digitalWalletItems && digitalWalletItems?.length > 1 && (
        <OLTabs
          className={classes.tiersHeaderWrapper}
          items={digitalWalletItems?.map((item) => {
            return {
              name: item.label,
              id: item.id,
            };
          })}
          maxLabelLength={30}
          onChange={(_, tab: string) => {
            setSelectedWalletId(tab);
          }}
          value={selectedWalletId}
        />
      )}
      <OLPaper noPadding>
        {selectedWalletId ? (
          <>
            <div className={classes.walletDetails}>
              <div
                className={classes.walletName}
                data-test-id="single-member-wallet-name"
              >
                {walletType?.name} ({walletType?.unitPluralName})
                <StatusValue status={!!walletType?.active} />
              </div>
              <Typography
                className={classes.walletActivePoints}
                data-test-id="single-member-active-unit-value"
              >
                {formatNumberValue(walletAccount?.activeUnits)}
                <span data-test-id="single-member-active-unit">
                  {message(
                    { id: "activeUnitSingularOrPlural" },
                    {
                      units: pickPluralizedUnit(
                        walletAccount?.activeUnits as number,
                        walletType?.unitSingularName,
                        walletType?.unitPluralName,
                      ),
                    },
                  )}
                </span>
              </Typography>
            </div>
            <div className={classes.divider} />
            <BoxSection items={walletUnitsItems} />
          </>
        ) : (
          <Skeleton height={100} />
        )}
      </OLPaper>
      {fullView && selectedWalletId && (
        <TransfersTable selectedWalletId={selectedWalletId} />
      )}
    </>
  );
}
